import React, { useEffect, useState, useRef, useId } from 'react';
import { motion } from 'framer-motion';
interface AnimatedGridPatternProps {
  width?: number;
  height?: number;
  x?: number;
  y?: number;
  strokeDasharray?: number;
  numSquares?: number;
  className?: string;
  maxOpacity?: number;
  duration?: number;
  repeatDelay?: number;
}
export function AnimatedGridPattern({
  width = 40,
  height = 40,
  x = -1,
  y = -1,
  strokeDasharray = 0,
  numSquares = 50,
  className = '',
  maxOpacity = 0.5,
  duration = 4,
  repeatDelay = 0.5,
  ...props
}: AnimatedGridPatternProps) {
  const id = useId();
  const containerRef = useRef<SVGSVGElement>(null);
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0
  });
  function getPos(): [number, number] {
    return [Math.floor(Math.random() * dimensions.width / width), Math.floor(Math.random() * dimensions.height / height)];
  }
  function generateSquares(count: number) {
    return Array.from({
      length: count
    }, (_, i) => ({
      id: i,
      pos: getPos()
    }));
  }
  const [squares, setSquares] = useState(() => generateSquares(numSquares));
  const updateSquarePosition = (squareId: number) => {
    setSquares(currentSquares => currentSquares.map(sq => sq.id === squareId ? {
      ...sq,
      pos: getPos()
    } : sq));
  };
  useEffect(() => {
    if (dimensions.width && dimensions.height) {
      setSquares(generateSquares(numSquares));
    }
  }, [dimensions, numSquares]);
  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height
        });
      }
    });
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    return () => {
      if (containerRef.current) {
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, []);
  return <svg ref={containerRef} aria-hidden="true" className={`pointer-events-none absolute inset-0 h-full w-full fill-blue-400/30 stroke-blue-400/30 dark:fill-blue-500/20 dark:stroke-blue-500/20 ${className}`} {...props} data-id="element-290">
      <defs data-id="element-291">
        <pattern id={id} width={width} height={height} patternUnits="userSpaceOnUse" x={x} y={y} data-id="element-292">
          <path d={`M.5 ${height}V.5H${width}`} fill="none" strokeDasharray={strokeDasharray} data-id="element-293" />
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${id})`} data-id="element-294" />
      <svg x={x} y={y} className="overflow-visible" data-id="element-295">
        {squares.map(({
        pos: [posX, posY],
        id: squareId
      }, index) => <motion.rect initial={{
        opacity: 0
      }} animate={{
        opacity: maxOpacity
      }} transition={{
        duration,
        repeat: 1,
        delay: index * 0.1,
        repeatType: 'reverse'
      }} onAnimationComplete={() => updateSquarePosition(squareId)} key={`${posX}-${posY}-${index}`} width={width - 1} height={height - 1} x={posX * width + 1} y={posY * height + 1} fill="currentColor" strokeWidth="0" data-id="element-296" />)}
      </svg>
    </svg>;
}